import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormBuilder, FormArray, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { HttpClient } from '@angular/common/http';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { CodeableConceptField, CodeField, GroupField, IndividualField, IndividualReferenceField, ReferenceFieldArray, formMetaData, codeableConceptDataType, ReferenceDataType, codingDataType, SingleCodeField, CodeableConceptFieldFromBackEnd } from '../../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../../shared/form-fields-select-data.service';
import { ErrorService } from '../../shared/error.service';
import { forkJoin, map, debounceTime, distinctUntilChanged, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ResourceDataReviewComponent } from '../../shared/resource-data-review/resource-data-review.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { backendEndPointToken, serviceRequestCategoriesToken } from '../../app.config';
import { StateService } from '../../shared/state.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessMessageComponent } from '../../shared/success-message/success-message.component';
import { UtilityService, ServiceRequestCodeOptionWithStatus } from '../../shared/utility.service';
import { EmphasisDirective } from '../../shared/directives/emphasis.directive';
import e from 'express';
import { AuthService } from '../../shared/auth/auth.service';
import { MatCardModule } from '@angular/material/card';
import { NotificationService } from '../../services/notification.service'
import { Bundle, Condition, Reference, CodeableConcept } from 'fhir/r4';
type FormFields = IndividualField | ReferenceFieldArray | CodeableConceptField | CodeField | IndividualReferenceField | GroupField;


@Component({
  selector: 'app-add-lab-requests',
  imports: [CommonModule, MatCardModule,
    EmphasisDirective,
    MatFormFieldModule, MatSelectModule, MatOptionModule, DynamicFormsV2Component, ReactiveFormsModule, MatIconModule,
    MatButtonModule, ResourceDataReviewComponent, MatAutocompleteModule, MatInputModule, MatDividerModule
  ],
  templateUrl: './add-lab-requests.component.html',
  styleUrl: './add-lab-requests.component.scss'
})
export class AddLabRequestsComponent {
  //constructor with optional mat dialog
  http = inject(HttpClient);
  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationService);
  private backendUrl = inject(backendEndPointToken);
  private serviceCategories = inject(serviceRequestCategoriesToken);
  private stateService = inject(StateService);
  auth = inject(AuthService);
  hasOrderTestsPermission = false;
  insufficientPrivilegeMessage = '';
  // New form for category and service request code selection
  categoryForm = this.fb.group({
    category: ['', Validators.required],
    serviceRequestCode: ['', Validators.required]
  });

  // Autocomplete options - initialized with hard-coded categories
  categoryOptions: any[] = [];
  serviceRequestOptions: any[] = [];
  filteredCategoryOptions: any[] = [];
  filteredServiceRequestOptions: any[] = [];

  // Loading states
  loadingCategories = false;
  loadingServiceRequests = false;

  private utilityService = inject(UtilityService);

  // Root form with a form array that mirrors fields in this.formFields
  requestForm: FormGroup = this.fb.group({
    items: this.fb.array([])
  });

  get items(): FormArray {
    return this.requestForm.get('items') as FormArray;
  }
  editFormFields: FormFields[] = [];

  // Builds one form group whose controls match this.formFields by fieldApiName
  private buildItemGroup(overrideValuesi?: Record<string, any>): FormGroup {
    let overrideValues = overrideValuesi ? { ...overrideValuesi } : {};
    if (!this.formFields) {
      return this.fb.group({});
    }
    this.editFormFields = [...this.formFields] as FormFields[];
    const controls: Record<string, FormControl> = {};
    (this.formFields || []).forEach(f => {
      const apiName = f.generalProperties.fieldApiName;
      let initial: any;
      if (overrideValues && Object.prototype.hasOwnProperty.call(overrideValues, apiName)) {
        initial = overrideValues[apiName]; // highest precedence
      } else if ('value' in f.generalProperties) {
        initial = (f as any).generalProperties.value;
      } else {
        initial = null;
      }
// $#$      // /* alert('initial value for ' + apiName + ' is ' + initial); */
      controls[apiName] = new FormControl(initial);
      delete (overrideValues?.[apiName]);
    });
    if (Object.keys(overrideValues).length > 0) {
      // Add any remaining override values not in formFields
      for (const [key, value] of Object.entries(overrideValues)) {
        if (key === 'category') {
          this.editFormFields?.push({
            generalProperties: {
              fieldApiName: 'ServiceRequestCategory',
              fieldName: 'Category',
              fieldLabel: 'Category',
              fieldType: 'SingleCodeField',
              isArray: false,
              isGroup: false,
              isHidden: this.data?.typeOfService ? true : false
            },
            data: this.serviceCategories.map(option => `${option.code}$#$${option.display}$#$${option.system}`)
            ,
          });
          controls['ServiceRequestCategory'] = new FormControl(value);
        }
        //code
        else if (key === 'code') {
          this.editFormFields?.push({
            generalProperties: {
              fieldApiName: 'ServiceRequestCode',
              fieldName: 'Service Request Code',
              fieldLabel: 'Service Request Code',
              fieldType: 'SingleCodeField',
              isArray: false,
              isGroup: false,

            },
            data: this.serviceRequestOptions

          }
          )
          controls['ServiceRequestCode'] = new FormControl(value);

        } else {
          controls[key] = new FormControl(value);
        }



      }
    }
    return this.fb.group(controls);
  }

  // Sync single field edits from ResourceDataReviewComponent
  onFieldEdited(index: number, evt: { fieldApiName: string; newValue: any; isArray?: boolean; arrayIndex?: number }) {
    const group = this.items.at(index) as FormGroup;
    if (!group) return;
    group.patchValue({ [evt.fieldApiName]: evt.newValue });
  }

  // Sync full resource object updates
  onResourceUpdated(index: number, updated: any) {
    const group = this.items.at(index) as FormGroup;
    if (!group) return;
    group.patchValue(updated);
  }

  removeRequest(index: number): void {
    if (index < 0 || index >= this.items.length) return;
    this.items.removeAt(index);
    this.items.markAsDirty();
    this.items.updateValueAndValidity();
    this.snackBar?.open('Request removed', 'Dismiss', { duration: 1500 });
  }

  dialogRef = inject(MatDialogRef<AddLabRequestsComponent>, { optional: true });
  data = inject<any>(MAT_DIALOG_DATA, { optional: true });

  constructor() { }
  submittedValues: any;




  isChosenOption = false;
  isMultiple = new FormControl<'Single' | 'Multiple' | null>(null);
  // Single/Group choice should only be shown for laboratory requests.
  isLaboratoryType = false;
  formMetaData?: formMetaData;
  formFields?: FormFields[];
  formFieldsDataService = inject(FormFieldsSelectDataService);
  errorService = inject(ErrorService);
  medicineForms: { formFields: FormFields[], formMetaData: formMetaData }[] = [

  ]

  ngOnInit() {
    // Initialize hard-coded categories
    this.categoryOptions = [...this.serviceCategories];
    this.filteredCategoryOptions = [...this.serviceCategories];
    this.checkOrderTestsPermission()
    // Setup category search with debouncing
    this.categoryForm.get('category')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      if (typeof value === 'string' && value.trim()) {
        this.searchCategory(value);
      } else if (value && typeof value === 'object') {
        // Category has been selected - update code options based on category
        this.onCategorySelected(value);
      }
    });

    // Setup service request code search with debouncing
    this.categoryForm.get('serviceRequestCode')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      if (typeof value === 'string' && value.trim()) {
        this.searchServiceRequestCode(value);
      } else if (value && typeof value === 'object') {
        // Service request code has been selected
        this.onServiceRequestCodeSelected(value);
      }
    });

    // Always load the dynamic form fields
    this.loadDynamicFormFields();
    if (this.data && !this.data.typeOfService) {
      this.isChosenOption = true;
    }

    // If a typeOfService is provided, only Laboratory should ask Single vs Group.
    // Other types should default to Single and proceed without prompting.
    const type = String(this.data?.typeOfService ?? '')
      .toString()
      .trim()
      .toLowerCase();
    this.isLaboratoryType = !!type && /lab|laboratory/.test(type);

    if (this.data?.typeOfService && !this.isLaboratoryType) {
      // Set Single and advance the dialog.
      // NOTE: loadDynamicFormFields() registers the valueChanges handler that sets isChosenOption.
      this.isMultiple.setValue('Single');
      this.isMultiple.disable({ emitEvent: false });
      this.isChosenOption = true;
    }

    // If a typeOfService is provided (currently only 'Laboratory'), preselect category and hide category input.
    if (this.data?.typeOfService) {
      const type = String(this.data.typeOfService).trim().toLowerCase();
      const match = this.categoryOptions.find((c: any) =>
        String(c?.code ?? '').toLowerCase() === type || String(c?.display ?? '').toLowerCase() === type
      );

      if (match) {
        this.selectedCategory = match.code;
        this.categoryForm.get('category')?.setValue(match, { emitEvent: false });
        this.categoryForm.get('category')?.disable({ emitEvent: false });

        // Clear service request code and load options for the selected category.
        this.categoryForm.get('serviceRequestCode')?.setValue('', { emitEvent: false });
        this.searchServiceRequestCode('');
      }
    }
  }
  /**
     * Check if user has permission to order tests
     */


  /**
   * Close the dialog
   */
  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private checkOrderTestsPermission(): void {
    this.hasOrderTestsPermission = this.auth.canOrderTests();

    if (!this.hasOrderTestsPermission) {
      this.insufficientPrivilegeMessage = 'You do not have permission to order lab tests. Please contact your administrator.';
      console.warn('⚠️ User lacks canOrderTests permission');
      return;
    }
  }
  // Track selected category for filtering codes
  selectedCategory: string | null = null;

  /**
   * Called when category is selected from autocomplete
   */
  onCategorySelected(category: any): void {
    console.log('Category selected:', category);
    this.selectedCategory = category.code;  // Store selected category code

    // Clear and reload service request codes filtered by category
    this.serviceRequestOptions = [];
    this.filteredServiceRequestOptions = [];
    this.categoryForm.get('serviceRequestCode')?.setValue('');

    // Trigger a fresh search with empty term to load all codes for this category
    this.searchServiceRequestCode('');
  }

  /**
   * Called when service request code is selected from autocomplete
   */
  onServiceRequestCodeSelected(code: any): void {
    console.log('Service request code selected:', code);
    // Set the code value in the dynamic form if code field exists
    if (this.formFields) {
      const codeField = this.formFields.find(f => f.generalProperties.fieldApiName === 'code');
      if (codeField) {
        // Update the code field value with the selected code
        codeField.generalProperties.value = `${code.code}$#$${code.display}$#$${code.system}`;
      }
    }
  }

  /**
   * Search for service request categories (client-side filtering of hard-coded categories)
   */
  searchCategory(searchTerm: string): void {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredCategoryOptions = [...this.categoryOptions];
      return;
    }

    this.filteredCategoryOptions = this.categoryOptions.filter((category: any) => {
      const display = category.display?.toLowerCase() || '';
      const code = category.code?.toLowerCase() || '';
      return display.includes(term) || code.includes(term);
    });
  }

  /**
   * Search for service request codes from backend using category-specific ValueSet
   * Constructs ValueSet URL by appending category name to base ServiceRequestCodes URL
   */
  searchServiceRequestCode(searchTerm: string): void {
    this.loadingServiceRequests = true;

    this.utilityService.searchServiceRequestCodeFromCodeSystem(searchTerm, this.selectedCategory).subscribe({
      next: (options: ServiceRequestCodeOptionWithStatus[]) => {
        this.serviceRequestOptions = options;
        this.filteredServiceRequestOptions = options;

      },
      complete: () => {
        this.loadingServiceRequests = false;
      },
      error: (err) => {
        console.error('Error searching service request codes:', err);
        this.loadingServiceRequests = false;
        this.errorService.openandCloseError('Failed to search service request codes');
      }
    });
  }

  /**
   * Display function for autocomplete
   */
  displayOption(option: any): string {
    return option?.display || '';
  }

  /**
   * Load dynamic form fields (original ngOnInit logic)
   */
  private loadDynamicFormFields(): void {
    this.formMetaData = <formMetaData>{
      // formName: 'Service Request (Lab Tests, e.t.c.)',
      // formDescription: "Use this form to order a lab test or any other medical services from your or other department",
      // submitText: 'Submit Request',

      formName: this.data && this.data.typeOfService ? `${String(this.data.typeOfService)[0].toUpperCase() + String(this.data.typeOfService).slice(1)} Service Request` : 'Service Request (Lab Tests, e.t.c.)',
      formDescription: "Use this form to order a " + (this.data.typeOfService ? this.data.typeOfService : "lab test or any other") + " medical services from your department or others",
      submitText: ` ${this.isMultiple.value && this.isMultiple.value == 'Multiple'
        ? 'Add' : 'Submit'} ${this.data && this.data.typeOfService ? this.data.typeOfService : ""} Request`,

    };
    this.isMultiple.valueChanges.subscribe((e: 'Single' | 'Multiple' | null) => {
      if (e !== null) {
        this.isChosenOption = true;
      }
      this.formMetaData = <formMetaData>{
        // formName: 'Service Request (Lab Tests, e.t.c.)',
        // formDescription: "Use this form to order a lab test or anyHere in the Earth Service.  other medical services from your or other department",
        // submitText: 'Submit Request',

        // formName: this.data && this.data.typeOfService ? `${this.data.typeOfService} Service Request` : 'Service Request (Lab Tests, e.t.c.)',
        formName: this.data && this.data.typeOfService ? `${String(this.data.typeOfService)[0].toUpperCase() + String(this.data.typeOfService).slice(1)} Service Request` : 'Service Request (Lab Tests, e.t.c.)',

        formDescription: "Use this form to order a " + (this.data.typeOfService ? this.data.typeOfService : "lab test or any other") + " medical services from your department or others",
        submitText: ` ${this.isMultiple.value && this.isMultiple.value == 'Multiple'
          ? 'Add' : 'Submit'} ${this.data && this.data.typeOfService ? String(this.data.typeOfService)[0].toUpperCase() + String(this.data.typeOfService).slice(1) : ""} Request`,

      }
    })

    const activePatientId = this.resolveActivePatientId();
    const condition$ = activePatientId
      ? this.http.get<Bundle<Condition>>(
        `${this.backendUrl}/Condition?patient=${activePatientId?.startsWith('Patient/') ? activePatientId : `Patient/${activePatientId}`}&_count=200`
      ).pipe(catchError(() => of(null)))
      : of(null);

    const reason$ = this.formFieldsDataService
      .getFormFieldSelectData('serviceRequest', 'reason')
      .pipe(catchError(() => of([])));

    forkJoin({
      status: this.formFieldsDataService.getFormFieldSelectData('serviceRequest', 'status'),
      intent: this.formFieldsDataService.getFormFieldSelectData('serviceRequest', 'intent'),
      code: this.formFieldsDataService.getFormFieldSelectData('serviceRequest', 'code'),
      performerType: this.formFieldsDataService.getFormFieldSelectData('serviceRequest', 'performerType'),
      priority: this.formFieldsDataService.getFormFieldSelectData('serviceRequest', 'priority'),
      reason: reason$,
      conditions: condition$
    }).subscribe({
      next: (g: any) => {
        console.log(g.medication);

        // Build formFields array - conditionally include code field based on typeOfService
        const fieldsArray: FormFields[] = [
          {
            generalProperties: {
              fieldApiName: 'status',
              fieldName: 'Status of Request',
              fieldLabel: 'Status of Request',
              value: 'active',
              isHidden: true,
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              fieldType: 'SingleCodeField',
              isArray: false,
              isGroup: false
            },
            data: "draft | active | on-hold | revoked | completed | entered-in-error | unknown".split(' | '),
            dataType: 'string'
          },
          {
            generalProperties: {
              fieldApiName: 'intent',
              fieldName: 'Intent of the Request',
              fieldLabel: 'Intent of the Request',
              value: 'order',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              validations: [{
                type: 'default',
                name: 'required',
                isFunction: false
              }],
              fieldType: 'SingleCodeField',
              isArray: false,
              isGroup: false
            },
            data: "proposal | plan | order | instance-order | option".split(' | '),
            dataType: 'string'
          }
        ];

        // Only add code field if typeOfService exists - otherwise it will come from category form
        //planned to not use it at all going forward
        if (false) {
          fieldsArray.push({
            generalProperties: {
              fieldApiName: 'code',
              fieldName: 'Service Requested',
              fieldLabel: 'Service Requested',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              validations: [{
                type: 'default',
                name: 'required',
                isFunction: false
              }],
              allowedOthers: true,
              fieldType: 'CodeableConceptFieldFromBackEnd',
              isArray: false,
              isGroup: false
            },
            data: g.code
          });
        }

        // Add remaining fields
        fieldsArray.push(
          {
            generalProperties: {
              fieldApiName: 'performerType',
              fieldName: 'Pratitioner Required',
              fieldLabel: 'Practitioner Required',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              fieldType: 'CodeableConceptField',
              isArray: false,
              isGroup: false
            },
            data: g.performerType
          },
          {
            generalProperties: {
              fieldApiName: 'priority',
              fieldName: 'Priority',
              fieldLabel: 'Priority',
              value: 'routine',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              fieldType: 'SingleCodeField',
              isArray: false,
              isGroup: false
            },
            data: "routine | urgent | asap | stat".split(' | '),
            dataType: 'string'
          }
        );

        const conditionOptions = this.extractConditionReferenceOptions(g.conditions);
        const reasonGroup = this.buildReasonGroupField(g.reason, conditionOptions);
        if (reasonGroup) {
          fieldsArray.push(reasonGroup);
        }

        fieldsArray.push(
          {
            generalProperties: {
              fieldApiName: 'note',
              fieldName: 'Additional Details',
              fieldLabel: 'Additional Details',
              fieldType: 'IndividualField',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              inputType: 'textarea',
              isArray: false,
              isGroup: false
            },
            data: '',
            dataType: 'string'
          }
        );

        this.formFields = fieldsArray;
      },
      error: (err: any) => {
        console.error('Error fetching medication data:', err);
        this.errorService.openandCloseError('Error fetching medication data. Please try again later.');
      }
    })
  }

  addMoreMedicineRequest() {

    if (this.formMetaData && this.formFields) {
      // this.medicineForms = [...this.medicineForms, { formMetaData: this.formMetaData, formFields: [...this.formFields] }];
      // Add another group mirroring current formFields
      this.items.push(this.buildItemGroup());
    }
  }


  @ViewChild('cref') cref!: DynamicFormsV2Component;

  serviceRequestIntentToStatus: Record<string, string> = {
    'proposal': 'draft',
    'plan': 'draft',
    'order': 'active',
    'instance-order': 'active',
    'option': 'draft'
  };

  private getCodingFromVariousStructure(value: any): { code: string, display?: string, system?: string } | { text: string } | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return { text: value };

    // Most of our pickers return { code, display, system }
    if (typeof value === 'object') {
      const code = (value.code ?? value.coding?.[0]?.code) as string;
      const display = (value.display ?? value.text ?? value.coding?.[0]?.display) as string;
      const system = (value.system ?? value.coding?.[0]?.system) as string;

      // if (code || display || system) {
      //   return `${code ?? ''}$#$${display ?? ''}$#$${system ?? ''}`;
      // }
      return {
        code,
        display,
        system

      }

      if (value.text) return { text: String(value.text) };
    }

    return { text: String(value) };
  }

  /**
   * For Multiple mode: each item already contains its own category/code.
   * This avoids incorrectly reusing the latest values from categoryForm.
   */
  private mergeItemGroupDataForSubmission(itemGroupValue: any): any {
    const mergedData = { ...(itemGroupValue ?? {}) };

    // These are the control names added by buildItemGroup(...)
    const categoryFromItem = mergedData.ServiceRequestCategory ?? mergedData.category;
    const codeFromItem = mergedData.ServiceRequestCode ?? mergedData.code;

    const encodedCategory = this.getCodingFromVariousStructure(categoryFromItem);
    const encodedCode = this.getCodingFromVariousStructure(codeFromItem);

    if (encodedCategory) mergedData.category = encodedCategory;
    if (encodedCode) mergedData.code = encodedCode;

    // Keep payload consistent with transformToServiceRequest expectations
    // delete mergedData.ServiceRequestCategory;
    // delete mergedData.ServiceRequestCode;

    return mergedData;
  }
  /**
   * Merge category and service request selections with form data
   */
  private mergeCategoryDataWithFormData(formData: any): any {
    const mergedData = { ...formData };

    // Add category if selected
    const categoryValue: any = this.categoryForm.get('category')?.value;
    console.log('Category value:', categoryValue);  // ✅ Use console.log instead

    if (categoryValue && categoryValue.code) {
      mergedData.category = {
        code: categoryValue.code,
        display: categoryValue.display,
        system: categoryValue.system || ''
      };
    } else if (categoryValue && categoryValue.text) {
      mergedData.category = categoryValue.text;
    } else if (categoryValue) {
      mergedData.category = { text: categoryValue };
    }

    // Add or update service request code if selected from category form
    const serviceCodeValue: any = this.categoryForm.get('serviceRequestCode')?.value;
    if (serviceCodeValue) {
      mergedData.code = typeof serviceCodeValue === 'object'
        ? serviceCodeValue
        : { text: serviceCodeValue };
    }

    console.log('Merged data:', mergedData);  // ✅ Use console.log instead
    return mergedData;
  }

  private resolveActivePatientId(): string | null {
    const encounter = this.stateService.currentEncounter.getValue();
    const encounterPatient = encounter?.['subject']?.reference || encounter?.patientId;
    const currentPatient = this.stateService.currentPatientId$.getValue();
    const resolverPatient = this.stateService.currentPatientIdFromResolver.getValue();

    const candidate = encounterPatient || currentPatient || resolverPatient;
    if (!candidate) {
      return null;
    }

    return candidate.startsWith('Patient/') ? candidate.split('/')[1] : candidate;
  }

  private extractConditionReferenceOptions(conditions: Bundle<Condition> | null): ReferenceDataType[] {
    if (!conditions?.entry?.length) {
      return [];
    }

    return conditions.entry
      .map(entry => entry.resource)
      .filter((resource): resource is Condition => !!resource?.id)
      .map(resource => ({
        reference: `Condition/${resource.id}`,
        display: resource.code?.text || resource.code?.coding?.[0]?.display || 'Patient condition'
      }));
  }

  private buildReasonGroupField(
    reasonOptions: codeableConceptDataType[] | string[] | undefined,
    conditionOptions: ReferenceDataType[]
  ): GroupField | null {
    const hasConditionOptions = conditionOptions.length > 0;
    const hasReasonOptions = Array.isArray(reasonOptions) && reasonOptions.length > 0;

    if (!hasConditionOptions && !hasReasonOptions) {
      return null;
    }

    const groupField: GroupField = {
      generalProperties: {
        fieldApiName: 'reason',
        fieldLabel: 'Reason for Request',
        fieldName: 'Reason for Request',
        fieldHint: 'Specify why this lab/service is being ordered',
        groupFieldsHint: 'Use an existing patient condition or search for a new reason code',
        moreHint: 'Document the clinical justification to help downstream teams',
        auth: {
          read: 'all',
          write: 'doctor, nurse'
        },
        fieldType: 'IndividualField',
        isArray: true,
        isGroup: true
      },
      keys: [],
      groupFields: {}
    };

    if (hasConditionOptions && hasReasonOptions) {
      groupField.keys.push('reasonSource');
      groupField.groupFields['reasonSource'] = {
        generalProperties: {
          fieldApiName: 'reasonSource',
          fieldName: 'Reason Source',
          fieldLabel: 'Where is the reason coming from?',
          fieldHint: 'Choose from patient conditions or enter a new reason',
          auth: {
            read: 'all',
            write: 'doctor, nurse'
          },
          fieldType: 'SingleCodeField',
          isArray: false,
          isGroup: false,
          inputType: 'select',
          controllingField: [
            {
              isAControlField: true,
              dependentFieldVisibilityTriggerValue: 'From Patient Condition',
              controlledFieldDependencyId: 'reasonReference'
            },
            {
              isAControlField: true,
              dependentFieldVisibilityTriggerValue: 'New Reason',
              controlledFieldDependencyId: 'reasonCode'
            }
          ]
        },
        data: ['From Patient Condition', 'New Reason']
      };
    }

    if (hasConditionOptions) {
      groupField.keys.push('reasonReference');
      const referenceGeneralProperties: any = {
        fieldApiName: 'reasonReference',
        fieldName: 'Reason from Patient Conditions',
        fieldLabel: 'Reason from Patient Conditions',
        fieldHint: 'Select from existing diagnoses recorded for this patient',
        auth: {
          read: 'all',
          write: 'doctor, nurse'
        },
        fieldType: 'IndividualReferenceField',
        isArray: false,
        isGroup: false
      };

      if (hasReasonOptions) {
        referenceGeneralProperties.dependence_id = 'reasonReference';
      }

      groupField.groupFields['reasonReference'] = {
        generalProperties: referenceGeneralProperties,
        data: conditionOptions
      };
    }

    if (hasReasonOptions) {
      groupField.keys.push('reasonCode');
      const reasonCodeGeneralProperties: any = {
        fieldApiName: 'reasonCode',
        fieldName: 'Reason Code',
        fieldLabel: 'Reason Code',
        fieldHint: 'Search terminology or add a custom reason code',
        auth: {
          read: 'all',
          write: 'doctor, nurse'
        },
        fieldType: 'CodeableConceptFieldFromBackEnd',
        isArray: false,
        isGroup: false,
        allowedOthers: true
      };

      if (hasConditionOptions) {
        reasonCodeGeneralProperties.dependence_id = 'reasonCode';
      }

      groupField.groupFields['reasonCode'] = <CodeableConceptFieldFromBackEnd>{
        generalProperties: reasonCodeGeneralProperties,
        data: reasonOptions ?? []
      };
    }

    return groupField;
  }

  private mapReasonEntries(raw: any): { reasonReference: Reference[]; reasonCode: CodeableConcept[] } {
    const reasonReference: Reference[] = [];
    const reasonCode: CodeableConcept[] = [];

    const rows = Array.isArray(raw) ? raw : [];
    for (const row of rows) {
      const rr = row?.reasonReference;
      if (rr) {
        if (typeof rr === 'string') {
          const trimmed = rr.trim();
          if (trimmed) {
            const reference = trimmed.includes('$#$') ? trimmed.split('$#$')[0].trim() : trimmed;
            if (reference) {
              reasonReference.push({ reference });
            }
          }
        } else if (typeof rr === 'object') {
          const reference = (rr.reference || rr.id || rr.resourceReference || '').toString().trim();
          if (reference) {
            const normalizedReference = reference.includes('/') ? reference : `Condition/${reference}`;
            const ref: Reference = { reference: normalizedReference };
            if (rr.display) {
              ref.display = rr.display;
            }
            reasonReference.push(ref);
          }
        }
      }

      const rc = this.toCodeableConcept(row?.reasonCode);
      if (rc) {
        reasonCode.push(rc);
      }
    }

    return { reasonReference, reasonCode };
  }

  private toCodeableConcept(value: any): CodeableConcept | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      if (trimmed.includes('$#$')) {
        const [code = '', display = '', system = ''] = trimmed.split('$#$');
        const coding = [{ code, display: display || code, system }];
        return {
          coding,
          text: display || code || undefined
        };
      }

      return { text: trimmed };
    }

    if (Array.isArray(value)) {
      const first = value[0];
      if (first) {
        return this.toCodeableConcept(first);
      }
    }

    if (typeof value === 'object') {
      const codingArray = Array.isArray(value.coding) ? value.coding : undefined;
      if (codingArray?.length) {
        const normalized = codingArray
          .map((c: { code?: string; display?: string; system?: string }) => ({
            code: (c?.code ?? '').toString(),
            display: (c?.display ?? c?.code ?? '').toString(),
            system: (c?.system ?? '').toString()
          }))
          .filter((c: { code?: string; display?: string; system?: string }) => c.code || c.display || c.system);

        if (normalized.length) {
          const concept: CodeableConcept = { coding: normalized };
          const textSource = value.text ?? normalized[0].display ?? normalized[0].code;
          if (textSource) {
            concept.text = textSource;
          }
          return concept;
        }
      }

      const code = value.code ?? value.coding?.[0]?.code;
      const display = value.display ?? value.coding?.[0]?.display ?? value.text;
      const system = value.system ?? value.coding?.[0]?.system;

      if (code || display || system) {
        const concept: CodeableConcept = {
          coding: [
            {
              code: (code ?? '').toString(),
              display: (display ?? code ?? '').toString(),
              system: (system ?? '').toString()
            }
          ]
        };
        const text = value.text ?? display ?? code;
        if (text) {
          concept.text = text.toString();
        }
        return concept;
      }

      if (value.text) {
        return { text: value.text.toString() };
      }
    }

    return null;
  }


  /**
   * Submit a single service request
   */
  submitSingleRequest(): void {
    // Additional validation for code when no typeOfService
    if (!this.data?.typeOfService) {
      const serviceCodeValue = this.categoryForm.get('serviceRequestCode')?.value;
      if (!serviceCodeValue) {
        this.errorService.openandCloseError('Please select a service request code');
        return;
      }
    }

    if (!this.cref?.aForm?.valid) {
      this.errorService.openandCloseError('Please fill in all required fields');
      return;
    }

    const formData = this.cref.aForm.value;
    const mergedData = this.mergeCategoryDataWithFormData(formData);

    // Validate required fields: intent, status, and code
    const missingFields: string[] = [];

    if (!mergedData.intent) {
      missingFields.push('Intent');
    }

    if (!mergedData.status) {
      missingFields.push('Status');
    }

    if (!mergedData.code) {
      missingFields.push('Service code');
    }

    if (missingFields.length > 0) {
      this.errorService.openandCloseError(
        `Missing required fields: ${missingFields.join(', ')}. Please complete the form.`
      );
      return;
    }

    // Process and submit the single request
    this.submitServiceRequest(mergedData);
  }
  snackBar = inject(MatSnackBar);
  /**
   * Process form values and merge with category selections
   */
  processValues(values: any): void {
    values.status = this.serviceRequestIntentToStatus[values.intent] || values.status;
    const mergedValues = this.mergeCategoryDataWithFormData(values);

    // Validate required fields before adding to items array
    const missingFields: string[] = [];

    if (!mergedValues.intent) {
      missingFields.push('Intent');
    }

    if (!mergedValues.status) {
      missingFields.push('Status');
    }

    if (!mergedValues.code) {
      missingFields.push('Service code');
    }

    if (missingFields.length > 0) {
      this.errorService.openandCloseError(
        `Missing required fields: ${missingFields.join(', ')}. Please complete the form before adding.`
      );
      return;
    }

    // If in Multiple mode, add to items array
    if (this.isMultiple.value === 'Multiple') {
      this.items.push(this.buildItemGroup(mergedValues));
      this.cref?.aForm?.reset();

      // Show success message for adding item
      this.snackBar.open(
        `Service request added (${this.items.length} total). Add more or submit all.`,
        'Dismiss',
        { duration: 2000 }
      );
    } else {
      // Single mode: submit directly
      this.submitServiceRequest(mergedValues);
    }
  }

  /**
   * Submit all requests in Multiple mode
   */
  submitAllRequests(): void {
    if (!this.requestForm.valid || this.items.length === 0) {
      this.errorService.openandCloseError('Please ensure all requests are valid');
      return;
    }

    // ✅ SINGLE allRequests declaration with proper logic
    const allRequests = this.items.controls.map((control, index) => {
      const requestData = this.mergeItemGroupDataForSubmission(control.value);

      // Validate required fields for each request
      const missingFields: string[] = [];

      if (!requestData.intent) {
        missingFields.push(`Request ${index + 1}: Intent`);
      }

      if (!requestData.status) {
        missingFields.push(`Request ${index + 1}: Status`);
      }

      if (!requestData.code) {
        missingFields.push(`Request ${index + 1}: Service code`);
      }

      return {
        data: requestData,
        valid: missingFields.length === 0,
        missingFields,
        index: index + 1
      };
    });

    // Check if any requests are invalid
    const invalidRequests = allRequests.filter(r => !r.valid);

    if (invalidRequests.length > 0) {
      const errorMessages = invalidRequests
        .map(r => r.missingFields.join(', '))
        .join('\n');

      this.errorService.openandCloseError(
        `Some requests are missing required fields:\n\n${errorMessages}\n\nPlease complete all requests before submitting.`
      );
      return;
    }

    // All requests are valid - submit each one
    const validRequests = allRequests.map(r => r.data);

    // If dialog mode, close with values and let parent handle submission
    if (this.dialogRef) {
      this.dialogRef.close({ values: validRequests });
      return;
    }

    // Build a bundle transaction for all requests
    const bundleEntries = validRequests.map((requestData, index) => {
      const serviceRequest = this.transformToServiceRequest(requestData);

      return {
        request: {
          method: 'POST',
          url: 'ServiceRequest'
        },
        resource: serviceRequest
      };
    });

    const bundle: any = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: bundleEntries
    };

    console.log('Submitting bundle transaction with all service requests:', bundle);

    this.http.post(`${this.backendUrl}`, bundle).subscribe({
      next: (response: any) => {
        console.log('All service requests submitted successfully:', response);

        // ✅ Trigger notifications for each successfully created request
        if (response.entry && Array.isArray(response.entry)) {
          response.entry.forEach((entry: any, index: number) => {
            // Check if the request was successful (201 Created)
            if (entry.resource && entry.response?.status?.startsWith('201')) {
              this.triggerServiceRequestNotification(
                entry.resource,
                validRequests[index]
              );
            }
          });
        }

        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: `Successfully submitted ${validRequests.length} service request${validRequests.length !== 1 ? 's' : ''}` },
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });

        // Clear the form after submission
        this.items.clear();
        this.cref?.aForm?.reset();
        this.categoryForm.reset();

        // Close dialog if opened as dialog
        if (this.dialogRef) {
          this.dialogRef.close(response);
        }
      },
      error: (err) => {
        console.error('Error submitting service requests:', err);
        this.errorService.openandCloseError(
          `Failed to submit requests: ${err.error?.issue?.[0]?.diagnostics || err.message || 'Unknown error'}`
        );
      }
    });
  }

  private triggerServiceRequestNotification(serviceRequest: any, requestData: any): void {
    console.log('🔔 [NOTIFICATION] Starting notification trigger...');
    console.log('📦 [NOTIFICATION] Service Request:', serviceRequest);
    console.log('📋 [NOTIFICATION] Request Data:', requestData);

    // Determine target roles based on category
    const category = requestData.category;
    let targetRoles: string[] = [];
    let title = 'New Service Request';

    console.log('🏷️ [NOTIFICATION] Raw category:', category);

    // Parse category - handle both string format and object format
    let categoryCode = '';
    if (typeof category === 'string') {
      console.log('📝 [NOTIFICATION] Category is string type');
      if (category.includes('$#$')) {
        categoryCode = category.split('$#$')[0];
        console.log('🔍 [NOTIFICATION] Extracted category code from encoded string:', categoryCode);
      } else {
        categoryCode = category;
        console.log('🔍 [NOTIFICATION] Using category as-is:', categoryCode);
      }
    } else if (category && typeof category === 'object') {
      console.log('📝 [NOTIFICATION] Category is object type');
      categoryCode = category.code || '';
      console.log('🔍 [NOTIFICATION] Extracted category code from object:', categoryCode);
    } else {
      console.log('⚠️ [NOTIFICATION] Category is null/undefined or unexpected type');
    }

    console.log('✅ [NOTIFICATION] Final category code:', categoryCode);

    // Map categories to roles
    switch (categoryCode.toLowerCase()) {
      case 'laboratory':
      case 'lab':
        targetRoles = ['lab', 'labtechnician', 'laboratory'];
        title = 'New Lab Test Request';
        console.log('🧪 [NOTIFICATION] Matched Laboratory category');
        break;
      case 'radiology':
      case 'imaging':
        targetRoles = ['radiology', 'radiologist', 'imaging'];
        title = 'New Imaging Request';
        console.log('📸 [NOTIFICATION] Matched Imaging/Radiology category');
        break;
      case 'pharmacy':
        targetRoles = ['pharmacist', 'pharmacy'];
        title = 'New Pharmacy Request';
        console.log('💊 [NOTIFICATION] Matched Pharmacy category');
        break;
      case 'procedure':
        targetRoles = ['nurse', 'doctor'];
        title = 'New Procedure Request';
        console.log('🏥 [NOTIFICATION] Matched Procedure category');
        break;
      case 'consultation':
        targetRoles = ['doctor', 'specialist'];
        title = 'New Consultation Request';
        console.log('👨‍⚕️ [NOTIFICATION] Matched Consultation category');
        break;
      case 'surgery':
        targetRoles = ['doctor', 'surgeon'];
        title = 'New Surgery Request';
        console.log('🔪 [NOTIFICATION] Matched Surgery category');
        break;
      default:
        targetRoles = ['nurse', 'doctor'];
        title = 'New Service Request';
        console.log('❓ [NOTIFICATION] No specific category match, using default roles');
    }

    console.log('👥 [NOTIFICATION] Target roles:', targetRoles);
    console.log('📌 [NOTIFICATION] Notification title:', title);

    // Get code display for message
    let codeDisplay = 'Service';
    const code = requestData.code;

    console.log('🔢 [NOTIFICATION] Raw code:', code);

    if (typeof code === 'string') {
      console.log('📝 [NOTIFICATION] Code is string type');
      if (code.includes('$#$')) {
        codeDisplay = code.split('$#$')[1] || code.split('$#$')[0];
        console.log('🔍 [NOTIFICATION] Extracted code display from encoded string:', codeDisplay);
      } else {
        codeDisplay = code;
        console.log('🔍 [NOTIFICATION] Using code as-is:', codeDisplay);
      }
    } else if (code && typeof code === 'object') {
      console.log('📝 [NOTIFICATION] Code is object type');
      codeDisplay = code.display || code.text || code.code || 'Service';
      console.log('🔍 [NOTIFICATION] Extracted code display from object:', codeDisplay);
    } else {
      console.log('⚠️ [NOTIFICATION] Code is null/undefined, using default');
    }

    console.log('✅ [NOTIFICATION] Final code display:', codeDisplay);

    // ✅ Get organization ID from auth service
    const organizationId = this.auth.getOrganizationId();

    console.log('🏢 [NOTIFICATION] Organization ID:', organizationId);

    if (!organizationId) {
      console.warn('❌ [NOTIFICATION] No organization ID found, skipping notification');
      return;
    }

    // Get patient ID for additional context
    const patientId = serviceRequest.subject?.reference?.split('/')[1] || '';
    console.log('🧑 [NOTIFICATION] Patient ID:', patientId);

    // Get current user for requester info
    const currentUser = this.auth.getCurrentUser();
    const requesterName = currentUser?.name || currentUser?.display || 'Unknown';

    console.log('👤 [NOTIFICATION] Current user:', currentUser);
    console.log('👤 [NOTIFICATION] Requester name:', requesterName);
    console.log('🆔 [NOTIFICATION] Requester ID:', currentUser?.id);

    const notificationPayload = {
      resourceType: 'ServiceRequest',
      resourceId: serviceRequest.id || '',
      organizationId: organizationId,
      targetRoles: targetRoles,
      title: title,
      message: `${requesterName} requested: ${codeDisplay}`,
      sendEmail: false,
      data: {
        priority: requestData.priority || 'routine',
        patientId: patientId,
        category: categoryCode,
        intent: requestData.intent || 'order',
        requesterName: requesterName,
        requesterId: currentUser?.id || ''
      }
    };

    console.log('📤 [NOTIFICATION] Full notification payload:', JSON.stringify(notificationPayload, null, 2));
    console.log('🚀 [NOTIFICATION] Sending notification request...');

    this.notificationService.triggerNotification(notificationPayload).subscribe({
      next: (response) => {
        console.log('✅ [NOTIFICATION] Notification sent successfully!');
        console.log('📥 [NOTIFICATION] Server response:', response);
        console.log(`✅ [NOTIFICATION] Notification sent to: ${targetRoles.join(', ')} for ${codeDisplay}`);
      },
      error: (err) => {
        console.error('❌ [NOTIFICATION] Failed to send notification');
        console.error('❌ [NOTIFICATION] Error details:', err);
        console.error('❌ [NOTIFICATION] Error status:', err.status);
        console.error('❌ [NOTIFICATION] Error message:', err.message);
        console.error('❌ [NOTIFICATION] Error response:', err.error);
        // Don't show error to user - notification failure shouldn't block the main action
      }
    });

    console.log('🏁 [NOTIFICATION] Notification trigger completed');
  }


  /**
   * Submit a single service request to the backend
   */
  private submitServiceRequest(requestData: any): void {
    // Final validation before transformation
    if (!requestData.intent || !requestData.status || !requestData.code) {
      this.errorService.openandCloseError('Cannot submit: Missing required fields (intent, status, or code)');
      return;
    }

    // submitAllRequests() {
    //   const allValues = this.items.value;
    if (this.dialogRef) {
      this.dialogRef.close({ values: [requestData] });
      return;
    }

    // Transform to FHIR ServiceRequest format
    const serviceRequest = this.transformToServiceRequest(requestData);

    this.http.post(`${this.backendUrl}/ServiceRequest`, serviceRequest).subscribe({
      next: (response) => {
        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: 'Service request submitted successfully' },
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });

        this.triggerServiceRequestNotification(response, requestData);

        // Close dialog if opened as dialog
        if (this.dialogRef) {
          this.dialogRef.close(response);
        }
      },
      error: (err) => {
        console.error('Error submitting service request:', err);
        this.errorService.openandCloseError('Failed to submit service request');
      }
    });
  }



  /**
   * Transform form data to FHIR ServiceRequest resource
   */
  private transformToServiceRequest(data: any): any {
    // Parse the $#$ encoded values
    const parseEncodedValue = (value: string | null | undefined) => {
      if (!value || typeof value !== 'string') return null;
      const parts = value.split('$#$');
      return {
        code: parts[0] || '',
        display: parts[1] || '',
        system: parts[2] || ''
      };
    };

    const category = parseEncodedValue(data.category);
    const code = parseEncodedValue(data.code);
    const currentPatientId = this.stateService.currentPatientId$.getValue();
    const currentEncounter = this.stateService.currentEncounter.getValue();
    const { reasonReference, reasonCode } = this.mapReasonEntries(data?.reason);

    return {
      resourceType: 'ServiceRequest',
      status: data.status || 'active',
      intent: data.intent || 'order',
      category: category ? [{
        coding: [{
          code: category.code,
          display: category.display,
          system: category.system
        }],
        text: category.display
      }] : undefined,
      code: code ? {
        coding: [{
          code: code.code,
          display: code.display,
          system: code.system
        }],
        text: code.display
      } : undefined,
      subject: data.subject || (currentPatientId ? { reference: `Patient/${currentPatientId}` } : undefined),
      encounter: data.encounter || (currentEncounter?.id ? { reference: `Encounter/${currentEncounter.id}` } : undefined),
      occurrenceDateTime: data.occurrenceDateTime || new Date().toISOString(),
      authoredOn: new Date().toISOString(),
      performerType: data.performerType ? [parseEncodedValue(data.performerType)] : undefined,
      priority: data.priority || 'routine',
      note: data.note ? [{
        text: data.note
      }] : undefined,
      ...(reasonReference.length ? { reasonReference } : {}),
      ...(reasonCode.length ? { reasonCode } : {})
    };
  }
}

// NOTE: This component is referenced as the closest pattern for AddServiceRequestComponent:
// - category selection (ServiceRequest.category)
// - code selection (ServiceRequest.code)
// - optional patient/encounter context
// - status updates when results are posted
